import { useEffect, useState } from 'react';
import { Camera, ChevronLeft, ChevronRight, MessageCircle, Play, Quote, Send, Users } from 'lucide-react';
import { parseFileUrl, queryAllClass, queryClass, type ParseRecord } from '../lib/parse';
import { approvedSocialLinks, type SocialLinkRecord } from '../lib/socialLinks';

type FeatureStory = ParseRecord & {
  DisplayName?: string;
  DisplayRole?: string;
  StoryText?: string;
  TestimonialText?: string;
  Image?: unknown;
  ShowInHomeHighlights?: boolean;
  ShowInHomeTestimonials?: boolean;
  ConsentConfirmed?: boolean;
  ImageRightsConfirmed?: boolean;
};

const channelIcons = { YouTube: Play, WhatsApp: MessageCircle, Instagram: Camera, Facebook: Users, Telegram: Send } as const;

export function HomeSocialProof() {
  const [highlights, setHighlights] = useState<FeatureStory[]>([]);
  const [testimonials, setTestimonials] = useState<FeatureStory[]>([]);

  useEffect(() => {
    let active = true;
    void queryAllClass('elearning_Story', {
        keys: ['DisplayName', 'DisplayRole', 'StoryText', 'TestimonialText', 'Image', 'ShowInHomeHighlights', 'ShowInHomeTestimonials', 'ConsentConfirmed', 'ImageRightsConfirmed'],
        order: '-createdAt',
        where: (query) => { query.equalTo('ConsentConfirmed', true); query.orWhere({ ShowInHomeHighlights: true }, { ShowInHomeTestimonials: true }); },
      }).catch(() => [] as ParseRecord[]).then((storyRows) => {
      if (!active) return;
      const rows = storyRows as FeatureStory[];
      setHighlights(rows.filter((story) => story.ShowInHomeHighlights === true && story.ConsentConfirmed === true && story.ImageRightsConfirmed === true && Boolean(story.DisplayName?.trim() && story.DisplayRole?.trim() && parseFileUrl(story.Image))));
      setTestimonials(rows.filter((story) => story.ShowInHomeTestimonials === true && story.ConsentConfirmed === true && Boolean(story.DisplayName?.trim() && story.TestimonialText?.trim()) && (!parseFileUrl(story.Image) || story.ImageRightsConfirmed === true)));
    });
    return () => { active = false; };
  }, []);

  if (!highlights.length && !testimonials.length) return null;
  return <div className="home-social-proof">
    {highlights.length > 0 && <RotatingFeature title="Learners and teachers" kind="highlight" stories={highlights} />}
    {testimonials.length > 0 && <RotatingFeature title="What students say" kind="testimonial" stories={testimonials} />}
  </div>;
}

export function HomeSocialLinks() {
  const [socialLinks, setSocialLinks] = useState<ReturnType<typeof approvedSocialLinks>>([]);

  useEffect(() => {
    let active = true;
    void queryClass('elearning_SocialLinks', {
        limit: 1,
        order: '-updatedAt',
        keys: ['IsPublished', 'YouTube', 'YouTubeApproved', 'WhatsApp', 'WhatsAppApproved', 'Instagram', 'InstagramApproved', 'Facebook', 'FacebookApproved', 'Telegram', 'TelegramApproved'],
        where: (query) => query.equalTo('IsPublished', true),
      }).catch(() => [] as ParseRecord[]).then((linkRows) => {
      if (!active) return;
      setSocialLinks(approvedSocialLinks(linkRows[0] as SocialLinkRecord | undefined));
    });
    return () => { active = false; };
  }, []);

  if (!socialLinks.length) return null;
  return <section className="home-social-links"><h2>Connect with AyurGuide</h2><div>{socialLinks.map((link) => {
      const Icon = channelIcons[link.key];
      return <a key={link.key} href={link.url} target="_blank" rel="noopener noreferrer"><Icon size={16} /><span>{link.label}</span><small className="sr-only">opens in a new tab</small></a>;
    })}</div></section>;
}

function RotatingFeature({ title, kind, stories }: { title: string; kind: 'highlight' | 'testimonial'; stories: FeatureStory[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);
  useEffect(() => {
    if (paused || reducedMotion || stories.length < 2) return;
    const timer = window.setInterval(() => setIndex((current) => (current + 1) % stories.length), 7000);
    return () => window.clearInterval(timer);
  }, [paused, reducedMotion, stories.length]);

  const story = stories[index % stories.length];
  const imageUrl = parseFileUrl(story.Image);
  const move = (step: number) => setIndex((current) => (current + step + stories.length) % stories.length);
  return <section className={`home-feature home-feature-${kind}`} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPaused(false); }} aria-roledescription="carousel" aria-label={title}>
    <div className="home-feature-heading"><h2>{title}</h2>{stories.length > 1 && <div className="home-feature-controls"><button type="button" onClick={() => move(-1)} aria-label={`Previous ${title.toLowerCase()}`}><ChevronLeft size={18} /></button><span aria-live="polite">{index + 1} / {stories.length}</span><button type="button" onClick={() => move(1)} aria-label={`Next ${title.toLowerCase()}`}><ChevronRight size={18} /></button></div>}</div>
    <article className="home-feature-slide" aria-live="polite">
      {imageUrl && <img src={imageUrl} alt={`${story.DisplayName}, ${story.DisplayRole}`} loading="lazy" />}
      <div><strong>{story.DisplayName}</strong><small>{story.DisplayRole}</small>{kind === 'testimonial' ? <blockquote><Quote size={16} aria-hidden="true" />{story.TestimonialText}</blockquote> : story.StoryText ? <p>{plainText(story.StoryText)}</p> : null}</div>
    </article>
  </section>;
}

function plainText(value: string) {
  const doc = new DOMParser().parseFromString(value, 'text/html');
  return (doc.body.textContent || '').trim();
}
